import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { isEqual } from 'lodash';
import NewValueContainer from '../../search/widgets/filters/react-select-custom-child-components/new-value-container';
import CheckBoxOption from '../../search/widgets/filters/react-select-custom-child-components/checkbox-option';
import { FacetObject } from '../../search/admin-dashboard-tables/interfaces';
import MenuList from '../../search/widgets/filters/MenuList';
import MenuWithFooterButton
  from '../../search/widgets/filters/react-select-custom-child-components/MenuWithFooterButton';

interface Props {
  options: Array<FacetObject>,
  extraCss?: string,
  placeholder: string,
  label: string,
  selectedValue: Array<FacetObject>,
  setSelectedValue: (selectedValue: Array<FacetObject>) => void,
  applyFilters: () => void,
  isDirty?: boolean,
  isFullWidth?: boolean,
}

const CustomAgeFilter: React.FC<Props> = ({
  options,
  extraCss,
  placeholder,
  label,
  selectedValue,
  setSelectedValue,
  applyFilters,
  isDirty,
  isFullWidth = false,
}) => {
  const attribute = 'birth_date';
  // Set component length based on the placeholder
  const length = `${(9 * placeholder.length) + 60}px`;

  // Custom sub-component styles
  const selectStyles = {
    control: (styles) => {
      return {
        ...styles,
        width: isFullWidth ? '100%' : length,
      };
    },
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: 'white',
        color: 'inherit',
      };
    },
  };

  const containerRef = useRef<HTMLDivElement>(null);
  // Is the select box focused (and open?)
  const [isFocused, setIsFocused] = useState(false);

  /**
   * Open or close the menu any time a click event is registered. Based
   * on the following conditions:
   *
   * Close the component and reset to empty search if
   * 1) The click is outside the component
   * 2) The menu (options dropdown) is not open
   * 3) The click is outside the menu
   *
   * Otherwise, keep it open
   */
  const onDomClick = (e) => {
    if (!containerRef || !containerRef.current) {
      return;
    }

    const menu = containerRef.current.querySelector('.algolia-select__menu');

    if (
      !containerRef.current.contains(e.target)
      || !menu
      || !menu.contains(e.target)
    ) {
      setIsFocused(false);
    } else {
      setIsFocused(true);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', onDomClick);

    return () => {
      document.removeEventListener('mousedown', onDomClick);
    };
  }, []);

  /**
   * If a select-dropdown menu is opened, but it is not this menu,
   * close this menu.
   *
   * There is currently an issue with events bubbling when <SelectDropdown> component
   * controls are clicked, and so a custom event is emitted instead.
   *
   * This is a temporary work-around, and a long-term task exists that will involve
   * re-writing the <SelectDropdown> component:
   * cf. https://schweb.atlassian.net/browse/LPBUGS-1453
   *
   * @param event
   */
  const onSelectMenuOpen = (event) => {
    if (event?.detail?.emitter !== attribute) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener('select-dropdown-opened', onSelectMenuOpen);

    return () => {
      document.removeEventListener('select-dropdown-opened', onSelectMenuOpen);
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Select
        aria-label={label || placeholder}
        className={`algolia-select ${extraCss}`}
        classNamePrefix="algolia-select"
        value={selectedValue.length > 0 ? selectedValue[0] : selectedValue}
        components={{
          Menu: MenuWithFooterButton,
          ValueContainer: NewValueContainer,
          Option: CheckBoxOption,
          MenuList,
        }}
        onChange={(event) => {
          setIsFocused(false);
          if (event) {
            const currentValue = selectedValue.length > 0 ? selectedValue[0] : selectedValue;
            if (isEqual(currentValue, event)) {
              setSelectedValue([]);
            } else {
              setSelectedValue([event]);
            }
          }
        }}
        isSearchable={false}
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        name={label}
        menuIsOpen={isFocused || undefined}
        isFocused={isFocused || undefined}
        onMenuClose={() => setIsFocused(false)}
        onMenuOpen={() => {
          setIsFocused(true);
          containerRef.current?.dispatchEvent(new CustomEvent('select-dropdown-opened', {
            bubbles: true,
            detail: {
              emitter: attribute,
            },
          }));
        }}
        setIsFocused={setIsFocused}
        closeMenuOnSelect={false}
        // These props are used in the `<MenuWithFooterButton>`.
        // They can be accessed via the `props.selectProps` object.
        onPressFooterButton={applyFilters}
        footerButtonText="Apply"
        hideFooterButton={!isDirty}
      />
    </div>
  );
};

export default CustomAgeFilter;
